import { useCallback, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { EmailType } from "@productlane/db/browser"
import { generateId } from "@productlane/lib/src/browser"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { IssueSelectIssue, ProjectSelectProject } from "./issue-select"
import { generateId as generateProjectId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"
import {
	customerNeedBody,
	issueLinkArgs,
	projectLinkArgs,
} from "../../lib/link-args"
import { useThreadContext } from "../../thread-context"

interface Visibility {
	isVisible?: boolean
	notUpdateVisibility?: boolean
}

/** Links an issue or project to the open thread: one path for the picker and for suggestions. */
export function useLinkThreadTarget() {
	const { thread } = useThreadContext()
	const zero = useZero()
	const emails = thread.emails
	const slackMessages = thread.slackMessages

	// Thread relations hold only the newest rows; the true first message comes
	// from the oldest-rows query, capped rows are the fallback while it syncs.
	const [oldestMessages] = useQuery(
		queries.threadOldestMessages({ threadId: thread.id }),
		{ ttl: "10m" },
	)
	const firstInboundEmail = useMemo(
		() =>
			oldestMessages?.emails[0] ??
			emails
				?.filter((email) => email.type === EmailType.INBOUND)
				.sort((a, b) => a.createdAt - b.createdAt)[0],
		[oldestMessages, emails],
	)
	const firstSlackMessage = useMemo(
		() =>
			(oldestMessages?.slackMessages ?? slackMessages ?? [])
				.filter((msg) => msg.type === EmailType.INBOUND)
				.sort((a, b) => a.createdAt - b.createdAt)[0],
		[oldestMessages, slackMessages],
	)
	const latestOutboundEmail = useMemo(
		() =>
			emails
				?.filter((email) => email.type === EmailType.OUTBOUND)
				.sort((a, b) => b.createdAt - a.createdAt)[0],
		[emails],
	)
	const latestSlackMessage = useMemo(
		() =>
			slackMessages
				?.filter((msg) => msg.type === EmailType.OUTBOUND)
				.sort((a, b) => b.createdAt - a.createdAt)[0],
		[slackMessages],
	)

	const getBody = useCallback(
		() =>
			customerNeedBody({
				text: thread.text,
				origin: thread.origin,
				firstInboundEmail,
				latestOutboundEmail,
				firstSlackMessage,
				latestSlackMessage,
			}),
		[
			thread.text,
			thread.origin,
			firstInboundEmail,
			latestOutboundEmail,
			firstSlackMessage,
			latestSlackMessage,
		],
	)

	const linkIssue = useCallback(
		(issue: IssueSelectIssue, visibility?: Visibility) =>
			// Note: This is a server-side signal mutation
			mutateOk(
				zero.mutate(
					mutators.issues.link(
						issueLinkArgs({
							thread,
							issue,
							body: getBody(),
							newIssueId: generateId(),
							visibility,
						}),
					),
				),
			),
		[thread, zero, getBody],
	)

	const linkProject = useCallback(
		(project: ProjectSelectProject, visibility?: Visibility) =>
			mutateOk(
				zero.mutate(
					mutators.projects.link(
						projectLinkArgs({
							thread,
							project,
							body: getBody(),
							newProjectId: generateProjectId(),
							visibility,
						}),
					),
				),
			),
		[thread, zero, getBody],
	)

	return { linkIssue, linkProject }
}
